/**
 * Filter Variant Images, Blade Count, and Light Options
 * 
 * This script handles updating product images based on housing color filter selections
 * and filtering visible variants based on blade count and light option selections
 * for Cormac and Ravian fan collections.
 */

(function() {
  'use strict';

  // Configuration
  const FAN_COLLECTIONS = ['cormac', 'ravian'];
  const HOUSING_COLOR_MAP = {
    'black': 'matte-black',
    'nickel': 'brushed-nickel', 
    'white': 'matte-white'
  };

  // State management
  let currentHousingFilter = null;
  let currentBladeFilter = null;
  let currentLightFilter = null;
  let productDataCache = new Map();
  let originalImages = new Map();

  /**
   * Initialize the filter variant image system
   */
  function init() {
    // Only run on collection pages
    if (!isCollectionPage()) return;
    
    // Check if this is a fan collection
    if (!isFanCollection()) return;

    // Listen for filter applications
    document.addEventListener('filters-applied', handleFiltersApplied);
    
    // Listen for initial page load
    document.addEventListener('DOMContentLoaded', function() {
      setupHousingColorRadioBehavior();
      setupBladeCountRadioBehavior();
      setupLightOptionRadioBehavior();
      checkCurrentFilters();
      preloadProductData();
      updateVariantVisibility();
    });

    // Check filters on initial load
    setupHousingColorRadioBehavior();
    setupBladeCountRadioBehavior();
    setupLightOptionRadioBehavior();
    checkCurrentFilters();
    preloadProductData();
    
    // Apply variant visibility on initial load
    updateVariantVisibility();
  }

  /**
   * Check if we're on a collection page
   */
  function isCollectionPage() {
    return document.querySelector('.template-collection') || 
           document.querySelector('[data-section-type="collection-template"]');
  }

  /**
   * Check if this is a fan collection
   */
  function isFanCollection() {
    const collectionHandle = getCollectionHandle();
    return FAN_COLLECTIONS.some(fan => collectionHandle && collectionHandle.includes(fan));
  }

  /**
   * Get the current collection handle
   */
  function getCollectionHandle() {
    // Try to get from URL
    const urlMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (urlMatch) return urlMatch[1];
    
    // Try to get from page data
    const collectionData = document.querySelector('[data-collection-handle]');
    if (collectionData) return collectionData.dataset.collectionHandle;
    
    return null;
  }

  /**
   * Setup housing color filter to behave like radio buttons (single selection)
   */
  function setupHousingColorRadioBehavior() {
    // Find the housing color filter section
    const housingColorFilter = findHousingColorFilter();
    if (!housingColorFilter) return;
    
    // Get all checkboxes in the housing color filter
    const checkboxes = housingColorFilter.querySelectorAll('input[type="checkbox"]');
    
    checkboxes.forEach(checkbox => {
      // Remove any existing listeners to avoid duplicates
      checkbox.removeEventListener('change', handleHousingColorChange);
      checkbox.addEventListener('change', handleHousingColorChange);
    });
  }
  
  /**
   * Handle housing color checkbox change to ensure only one is selected
   */
  function handleHousingColorChange(event) {
    const checkbox = event.target;
    
    if (checkbox.checked) {
      // Find the housing color filter section
      const housingColorFilter = findHousingColorFilter();
      if (!housingColorFilter) return;
      
      // Uncheck all other checkboxes in this filter
      const otherCheckboxes = housingColorFilter.querySelectorAll('input[type="checkbox"]');
      otherCheckboxes.forEach(otherCheckbox => {
        if (otherCheckbox !== checkbox && otherCheckbox.checked) {
          otherCheckbox.checked = false;
          // Trigger change event on the unchecked checkbox to update the filter
          otherCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  
  /**
   * Find the housing color filter element
   */
  function findHousingColorFilter() {
    // Try multiple selectors to find the housing color filter
    const selectors = [
      '.cc-filter[data-filter-label*="housing-color"]',
      '.cc-filter[data-filter-label*="housing"]',
      '.cc-filter[data-filter-label*="color"]'
    ];
    
    for (const selector of selectors) {
      const filter = document.querySelector(selector);
      if (filter) {
        // Verify this is actually a housing color filter by checking for expected options
        const labels = filter.querySelectorAll('label');
        let isHousingColorFilter = false;
        
        labels.forEach(label => {
          const labelText = label.textContent.toLowerCase().trim();
          if (labelText.includes('black') || labelText.includes('nickel') || labelText.includes('white')) {
            isHousingColorFilter = true;
          }
        });
        
        if (isHousingColorFilter) {
          return filter;
        }
      }
    }
    
    return null;
  }

  /**
   * Find the blade count filter element
   */
  function findBladeCountFilter() {
    // Try multiple selectors to find the blade count filter
    const selectors = [
      '.cc-filter[data-filter-label*="number-of-blades"]',
      '.cc-filter[data-filter-label*="blades"]',
      '.cc-filter[data-filter-label*="blade"]'
    ];
    
    // Check all filters and look for blade-related text
    const allFilters = document.querySelectorAll('.cc-filter');
    
    for (const filter of allFilters) {
      const filterLabel = filter.dataset.filterLabel;
      
      // Check if this might be a blade count filter
      if (filterLabel && (filterLabel.includes('blade') || filterLabel.includes('number'))) {
        // Verify by checking the actual options
        const labels = filter.querySelectorAll('label');
        let hasBladeOptions = false;
        
        labels.forEach(label => {
          const labelText = label.textContent.trim();
          // Check if this is a blade count option - could be just "3" or "5" or include "blade"
          if (labelText === '3' || labelText === '5' || 
              ((labelText.includes('3') || labelText.includes('5')) && labelText.toLowerCase().includes('blade'))) {
            hasBladeOptions = true;
          }
        });
        
        if (hasBladeOptions) {
          return filter;
        }
      }
    }
    
    // Try the original selector approach as fallback
    for (const selector of selectors) {
      const filter = document.querySelector(selector);
      if (filter) {
        return filter;
      }
    }
    
    return null;
  }

  /**
   * Find the light option filter element
   */
  function findLightOptionFilter() {
    // Check all filters and look for light-related text
    const allFilters = document.querySelectorAll('.cc-filter');
    
    for (const filter of allFilters) {
      const filterLabel = filter.dataset.filterLabel;
      
      // Check if this might be a light option filter
      if (filterLabel && (filterLabel.includes('light') || filterLabel.includes('lights'))) {
        // Verify by checking the actual options
        const labels = filter.querySelectorAll('label');
        let hasLightOptions = false;
        
        labels.forEach(label => {
          const labelText = label.textContent.trim();
          // Check if this is a light option - look for exact "With Light" or "Without Light" labels
          if (labelText === 'With Light' || labelText === 'Without Light' ||
              labelText.toLowerCase().includes('with light') || labelText.toLowerCase().includes('without light') ||
              labelText === '0' || labelText === '1' || labelText.toLowerCase() === 'yes' || labelText.toLowerCase() === 'no') {
            hasLightOptions = true;
          }
        });
        
        if (hasLightOptions) {
          return filter;
        }
      }
    }
    
    return null;
  }

  /**
   * Setup blade count filter to behave like radio buttons (single selection)
   */
  function setupBladeCountRadioBehavior() {
    // Find the blade count filter section
    const bladeCountFilter = findBladeCountFilter();
    if (!bladeCountFilter) return;
    
    // Get all checkboxes in the blade count filter
    const checkboxes = bladeCountFilter.querySelectorAll('input[type="checkbox"]');
    
    checkboxes.forEach(checkbox => {
      // Remove any existing listeners to avoid duplicates
      checkbox.removeEventListener('change', handleBladeCountChange);
      checkbox.addEventListener('change', handleBladeCountChange);
    });
  }

  /**
   * Setup light option filter to behave like radio buttons (single selection)
   */
  function setupLightOptionRadioBehavior() {
    // Find the light option filter section
    const lightOptionFilter = findLightOptionFilter();
    if (!lightOptionFilter) return;
    
    // Get all checkboxes in the light option filter
    const checkboxes = lightOptionFilter.querySelectorAll('input[type="checkbox"]');
    
    checkboxes.forEach(checkbox => {
      // Remove any existing listeners to avoid duplicates
      checkbox.removeEventListener('change', handleLightOptionChange);
      checkbox.addEventListener('change', handleLightOptionChange);
    });
  }

  /**
   * Handle blade count checkbox change to ensure only one is selected
   */
  function handleBladeCountChange(event) {
    const checkbox = event.target;
    
    if (checkbox.checked) {
      // Find the blade count filter section
      const bladeCountFilter = findBladeCountFilter();
      if (!bladeCountFilter) return;
      
      // Uncheck all other checkboxes in this filter
      const otherCheckboxes = bladeCountFilter.querySelectorAll('input[type="checkbox"]');
      otherCheckboxes.forEach(otherCheckbox => {
        if (otherCheckbox !== checkbox && otherCheckbox.checked) {
          otherCheckbox.checked = false;
          // Trigger change event on the unchecked checkbox to update the filter
          otherCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  /**
   * Handle light option checkbox change to ensure only one is selected
   */
  function handleLightOptionChange(event) {
    const checkbox = event.target;
    
    if (checkbox.checked) {
      // Find the light option filter section
      const lightOptionFilter = findLightOptionFilter();
      if (!lightOptionFilter) return;
      
      // Uncheck all other checkboxes in this filter
      const otherCheckboxes = lightOptionFilter.querySelectorAll('input[type="checkbox"]');
      otherCheckboxes.forEach(otherCheckbox => {
        if (otherCheckbox !== checkbox && otherCheckbox.checked) {
          otherCheckbox.checked = false;
          // Trigger change event on the unchecked checkbox to update the filter
          otherCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  /**
   * Handle when filters are applied
   */
  function handleFiltersApplied() {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      setupHousingColorRadioBehavior(); // Re-setup after DOM update
      setupBladeCountRadioBehavior(); // Re-setup blade count filter
      setupLightOptionRadioBehavior(); // Re-setup light option filter
      checkCurrentFilters();
      updateAllProductImages();
      updateVariantVisibility();
      
      // Re-initialize finish selector hover effects after DOM update
      if (window.initializeFinishSelectorHovers) {
        window.initializeFinishSelectorHovers();
      }
    }, 100);
  }

  /**
   * Check if an active filter element is related to blade count
   */
  function checkIfBladeFilter(filterElement) {
    // Check the URL parameter name associated with this filter
    const href = filterElement.getAttribute('href');
    if (href && (href.includes('blade') || href.includes('number'))) {
      return true;
    }
    
    // Check parent elements for blade-related context
    let parent = filterElement.parentElement;
    while (parent && parent !== document.body) {
      const text = parent.textContent.toLowerCase();
      if (text.includes('blade') || text.includes('number of blades')) {
        return true;
      }
      parent = parent.parentElement;
    }
    
    return false;
  }

  /**
   * Check if an active filter element is related to light options
   */
  function checkIfLightFilter(filterElement) {
    // Check the URL parameter name associated with this filter
    const href = filterElement.getAttribute('href');
    if (href && (href.includes('light') || href.includes('lights'))) {
      return true;
    }
    
    // Check parent elements for light-related context
    let parent = filterElement.parentElement;
    while (parent && parent !== document.body) {
      const text = parent.textContent.toLowerCase();
      if (text.includes('light') && !text.includes('highlight')) {
        return true;
      }
      parent = parent.parentElement;
    }
    
    return false;
  }

  /**
   * Check current active filters
   */
  function checkCurrentFilters() {
    // Look for active housing color filters
    const activeFilters = document.querySelectorAll('.cc-active-filter');
    let housingFilter = null;
    let bladeFilter = null;
    let lightFilter = null;
    
    activeFilters.forEach(filter => {
      const filterText = filter.textContent.toLowerCase();
      const filterTextTrimmed = filter.textContent.trim();
      
      // Check if this is a housing color filter
      Object.keys(HOUSING_COLOR_MAP).forEach(color => {
        if (filterText.includes(color)) {
          housingFilter = color;
        }
      });
      
      // Check if this is a blade count filter
      // The active filter might show just "3" or "5" or include "blade"
      const isBladeFilter = checkIfBladeFilter(filter);
      const isLightFilter = checkIfLightFilter(filter);
      
      // First check if it's just a number (3 or 5) for blade count
      if (filterTextTrimmed === '3' || filterTextTrimmed === '5') {
        // For fan collections, assume numeric filters are blade counts
        if (isBladeFilter || (isFanCollection() && !isLightFilter)) {
          bladeFilter = filterTextTrimmed;
        }
      } else if (filterText.includes('blade')) {
        // Extract the number from text containing "blade"
        const match = filterText.match(/(\d+)[\s-]?blade/i);
        if (match) {
          bladeFilter = match[1];
        }
      }
      
      // Check for light option filters
      if (isLightFilter || filterText.includes('light')) {
        // Handle various light option formats - match exact filter labels
        if (filterTextTrimmed === 'With Light' || filterText === 'with light') {
          lightFilter = 'with-light';
        } else if (filterTextTrimmed === 'Without Light' || filterText === 'without light') {
          lightFilter = 'without-light';
        } else if (filterTextTrimmed === '1' && isLightFilter) {
          lightFilter = 'with-light';
        } else if (filterTextTrimmed === '0' && isLightFilter) {
          lightFilter = 'without-light';
        } else if (filterTextTrimmed.toLowerCase() === 'yes' && isLightFilter) {
          lightFilter = 'with-light';
        } else if (filterTextTrimmed.toLowerCase() === 'no' && isLightFilter) {
          lightFilter = 'without-light';
        }
      }
    });
    
    // Also check checkbox states in housing color filter
    const housingColorFilter = findHousingColorFilter();
    if (housingColorFilter) {
      const colorCheckboxes = housingColorFilter.querySelectorAll('input[type="checkbox"]:checked');
      colorCheckboxes.forEach(checkbox => {
        const label = checkbox.nextElementSibling;
        if (label) {
          const labelText = label.textContent.toLowerCase().trim();
          Object.keys(HOUSING_COLOR_MAP).forEach(color => {
            if (labelText === color || labelText.includes(color)) {
              housingFilter = color;
            }
          });
        }
      });
    }
    
    // Check checkbox states in blade count filter
    const bladeCountFilter = findBladeCountFilter();
    if (bladeCountFilter) {
      const bladeCheckboxes = bladeCountFilter.querySelectorAll('input[type="checkbox"]:checked');
      
      bladeCheckboxes.forEach(checkbox => {
        const label = checkbox.nextElementSibling;
        if (label) {
          // Try to get just the filter label text without the count
          const filterLabelSpan = label.querySelector('.cc-filter-label');
          const labelText = filterLabelSpan ? filterLabelSpan.textContent.trim() : label.textContent.trim();
          // Extract the number from blade count labels
          // Handle both "3-blade", "3 blade" and just "3" formats
          if (labelText === '3' || labelText === '5') {
            // Direct numeric match
            bladeFilter = labelText;
          } else {
            // Try to extract from text with "blade"
            const match = labelText.match(/(\d+)[\s-]?blade/i);
            if (match) {
              bladeFilter = match[1];
            }
          }
        }
      });
    }
    
    // Check checkbox states in light option filter
    const lightOptionFilter = findLightOptionFilter();
    if (lightOptionFilter) {
      const lightCheckboxes = lightOptionFilter.querySelectorAll('input[type="checkbox"]:checked');
      
      lightCheckboxes.forEach(checkbox => {
        const label = checkbox.nextElementSibling;
        if (label) {
          // Try to get just the filter label text without the count
          const filterLabelSpan = label.querySelector('.cc-filter-label');
          const labelText = filterLabelSpan ? filterLabelSpan.textContent.trim() : label.textContent.trim();
          const labelTextLower = labelText.toLowerCase();
          
          // Handle various light option formats - match exact labels from the filter
          if (labelText === 'With Light' || labelTextLower === 'with light') {
            lightFilter = 'with-light';
          } else if (labelText === 'Without Light' || labelTextLower === 'without light') {
            lightFilter = 'without-light';
          } else if (labelText === '1' || labelTextLower === 'yes') {
            lightFilter = 'with-light';
          } else if (labelText === '0' || labelTextLower === 'no') {
            lightFilter = 'without-light';
          }
        }
      });
    }
    
    // Also check URL parameters for filter values
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value, key) => {
      if (key.includes('filter') && value) {
        const valueLower = value.toLowerCase();
        Object.keys(HOUSING_COLOR_MAP).forEach(color => {
          if (valueLower === color || valueLower.includes(color)) {
            housingFilter = color;
          }
        });
        
        // Check for blade count in URL - could be just "3" or "5" or include "blade"
        if (key.toLowerCase().includes('blade') || key.toLowerCase().includes('number')) {
          // If the key contains blade/number, the value might just be the number
          if (value === '3' || value === '5') {
            bladeFilter = value;
          }
        } else if (valueLower.includes('blade')) {
          // Check if value contains blade
          const match = valueLower.match(/(\d+)[\s-]?blade/);
          if (match) {
            bladeFilter = match[1];
          }
        } else if (value === '3' || value === '5') {
          // If we have a plain number, check if the key suggests it's blade-related
          if (key.includes('blade') || key.includes('number')) {
            bladeFilter = value;
          }
        }
        
        // Check for light option in URL
        if (key.toLowerCase().includes('light')) {
          if (valueLower.includes('with') || value === '1' || valueLower === 'yes') {
            lightFilter = 'with-light';
          } else if (valueLower.includes('without') || value === '0' || valueLower === 'no') {
            lightFilter = 'without-light';
          }
        }
      }
    });
    
    currentHousingFilter = housingFilter;
    currentBladeFilter = bladeFilter;
    currentLightFilter = lightFilter;
  }

  /**
   * Preload product data for all visible products
   */
  function preloadProductData() {
    const productBlocks = document.querySelectorAll('.product-block.fan-variant-item');
    const productHandles = new Set();
    
    productBlocks.forEach(block => {
      const link = block.querySelector('.product-block__image');
      if (link) {
        const handleMatch = link.href.match(/\/products\/([^?#]+)/);
        if (handleMatch) {
          productHandles.add(handleMatch[1]);
        }
      }
    });
    
    // Fetch product data for each unique handle
    productHandles.forEach(handle => {
      if (!productDataCache.has(handle)) {
        fetch(`/products/${handle}.js`)
          .then(response => response.json())
          .then(data => {
            productDataCache.set(handle, data);
      })
      .catch(error => {
          });
      }
    });
  }

  /**
   * Update all product images based on current filter
   */
  function updateAllProductImages() {
    const productBlocks = document.querySelectorAll('.product-block.fan-variant-item');
    
    productBlocks.forEach(block => {
      if (currentHousingFilter) {
        updateProductImage(block, currentHousingFilter);
      } else {
        restoreOriginalImage(block);
      }
    });
  }

  /**
   * Update a single product image
   */
  function updateProductImage(productBlock, housingColor) {
    const imageContainer = productBlock.querySelector('.image-one');
    if (!imageContainer) return;
    
    const link = productBlock.querySelector('.product-block__image');
    if (!link) return;
    
    const handleMatch = link.href.match(/\/products\/([^?#]+)/);
    if (!handleMatch) return;
    
    const productHandle = handleMatch[1];
    const productData = productDataCache.get(productHandle);
    
    if (!productData) {
      // Fetch product data if not cached
      fetch(`/products/${productHandle}.js`)
        .then(response => response.json())
        .then(data => {
          productDataCache.set(productHandle, data);
          updateProductImageWithData(productBlock, data, housingColor);
        });
    } else {
      updateProductImageWithData(productBlock, productData, housingColor);
    }
  }

  /**
   * Update product image with fetched data
   */
  function updateProductImageWithData(productBlock, productData, housingColor) {
    const imageContainer = productBlock.querySelector('.image-one');
    if (!imageContainer) return;
    
    // Store original image if not already stored
    const blockId = productBlock.dataset.blockId || productBlock.querySelector('.product-block__image').href;
    if (!originalImages.has(blockId)) {
      originalImages.set(blockId, imageContainer.innerHTML);
    }
    
    // Get current variant info from the block
    const currentVariantId = productBlock.querySelector('[data-variant-id]')?.dataset.variantId;
    const currentVariant = productData.variants.find(v => v.id == currentVariantId);
    
    if (!currentVariant) {
      return;
    }
    
    // Find the finish option position
    let finishOptionPosition = -1;
    if (productData.options && Array.isArray(productData.options)) {
      productData.options.forEach((option, index) => {
        // Handle both string and object option formats
        if (option) {
          const optionName = typeof option === 'string' ? option : option.name;
          if (optionName && optionName.toLowerCase().includes('finish')) {
            finishOptionPosition = index;
          }
        }
      });
    } else {
      return;
    }
    
    if (finishOptionPosition === -1) {
      return;
    }
    
    // Find variant with matching finish and other options
    const targetFinish = HOUSING_COLOR_MAP[housingColor];
    let matchingVariant = null;
    
    productData.variants.forEach(variant => {
      // Check if finish matches
      const variantFinish = variant.options[finishOptionPosition];
      if (variantFinish && typeof variantFinish === 'string') {
        // Normalize the finish names for comparison
        const normalizedVariantFinish = variantFinish.toLowerCase().replace(/[^a-z]/g, '');
        const normalizedTargetFinish = targetFinish.toLowerCase().replace(/[^a-z]/g, '');
        
        if (normalizedVariantFinish === normalizedTargetFinish) {
          // Check if other options match (blade count, light, etc.)
          let otherOptionsMatch = true;
          
          if (variant.options && currentVariant.options) {
            variant.options.forEach((option, index) => {
              if (index !== finishOptionPosition && currentVariant.options[index] !== option) {
                otherOptionsMatch = false;
              }
            });
          }
          
          if (otherOptionsMatch && variant.available) {
            matchingVariant = variant;
          }
        }
      }
    });
    
    if (matchingVariant && matchingVariant.featured_media) {
      // Update the image
      const newImageSrc = matchingVariant.featured_media.preview_image.src.replace(/(_\d+x\d*\.)/g, '_600x.');
      const imageAlt = matchingVariant.featured_media.alt || matchingVariant.title || '';
      
      imageContainer.style.transition = 'opacity 0.3s ease';
      imageContainer.style.opacity = '0.8';
      
      setTimeout(() => {
        imageContainer.innerHTML = `
          <div class="rimage-outer-wrapper">
            <div class="rimage-wrapper lazyloaded" style="padding-top: 100%;">
              <img class="rimage__image lazyloaded fade-in" 
                   src="${newImageSrc}" 
                   alt="${imageAlt}"
                   style="opacity: 1;">
            </div>
          </div>
        `;
        imageContainer.style.opacity = '1';
      }, 150);
    }
  }

  /**
   * Restore original product image
   */
  function restoreOriginalImage(productBlock) {
    const imageContainer = productBlock.querySelector('.image-one');
    if (!imageContainer) return;
    
    const blockId = productBlock.dataset.blockId || productBlock.querySelector('.product-block__image').href;
    const originalImage = originalImages.get(blockId);
    
    if (originalImage) {
      imageContainer.style.transition = 'opacity 0.3s ease';
      imageContainer.style.opacity = '0.8';
      
      setTimeout(() => {
        imageContainer.innerHTML = originalImage;
        imageContainer.style.opacity = '1';
        
        // Trigger lazy loading if needed
        if (window.lazySizes) {
          const lazyElements = imageContainer.querySelectorAll('.lazyload');
          lazyElements.forEach(el => {
            window.lazySizes.loader.unveil(el);
          });
        }
      }, 150);
    }
  }

  /**
   * Update visibility of fan variant items based on blade count and light option filters
   */
  function updateVariantVisibility() {
    const fanVariantItems = document.querySelectorAll('.fan-variant-item');
    
    fanVariantItems.forEach(item => {
      const itemBladeCount = item.dataset.bladeCount;
      const itemLightOption = item.dataset.lightOption;
      
      let shouldShow = true;
      
      // Check blade count filter
      if (currentBladeFilter && itemBladeCount !== currentBladeFilter) {
        shouldShow = false;
      }
      
      // Check light option filter
      if (currentLightFilter && itemLightOption !== currentLightFilter) {
        shouldShow = false;
      }
      
      // Apply visibility
      if (shouldShow) {
        item.style.display = '';
        item.classList.remove('filter-hidden');
      } else {
        item.style.display = 'none';
        item.classList.add('filter-hidden');
      }
    });
  }

  // Initialize the system
  init();

})();
